import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { PRICING_FAQS, MOBILE_APP_FAQS, ENTERPRISE_FAQS } from '../constants';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const Faq: React.FC = () => {
    const { faqs } = useData();
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const { t } = useTranslation();

    const toggleFaq = (index: number) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    return (
        <div className="w-full bg-brand-light">
            <SEO
                pageCode="faq"
                title="Sıkça Sorulan Sorular"
                description="Pato Yazılım hizmetleri, süreçleri ve daha fazlası hakkında sıkça sorulan soruların cevaplarını bulun."
                link="/sss"
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
            </Helmet>
            <section className="pt-32 pb-12 bg-brand-dark text-center">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="inline-flex items-center gap-2 px-6 py-2.5 bg-white rounded-full text-brand-blue font-bold text-sm mb-8 shadow-lg shadow-brand-blue/20">
                        <HelpCircle size={18} />
                        <span>{t('faqPage.tag')}</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                        {t('common.faq')}
                    </h1>
                    <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                        {t('faqPage.heroDesc')}
                    </p>
                </div>
            </section>

            {/* General FAQ List */}
            <section className="py-12 bg-white">
                <div className="container mx-auto px-4 md:px-8 max-w-4xl">
                    <h2 className="text-2xl font-bold text-center text-brand-dark mb-8">Genel Sıkça Sorulan Sorular</h2>
                    <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                            <div key={idx} className={`border border-gray-200 rounded-lg overflow-hidden transition-colors duration-300 ${openFaq === idx ? 'bg-[#3e73fe] border-[#3e73fe]' : 'bg-white hover:border-brand-blue/30'}`}>
                                <button
                                    onClick={() => toggleFaq(idx)}
                                    className={`w-full flex justify-between items-center p-6 text-left transition-colors ${openFaq === idx ? 'text-white' : 'text-brand-dark'}`}
                                >
                                    <span className="font-medium text-lg">{faq.question}</span>
                                    {openFaq === idx ? <ChevronUp className="flex-shrink-0 ml-4" /> : <ChevronDown className="flex-shrink-0 ml-4" />}
                                </button>
                                <div className={`transition-all duration-300 ease-in-out ${openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <div className={`p-6 pt-0 ${openFaq === idx ? 'text-white' : 'text-gray-700'}`}>
                                        {faq.answer}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing FAQ List */}
            <section className="py-12 bg-brand-light">
                <div className="container mx-auto px-4 md:px-8 max-w-4xl">
                    <h2 className="text-2xl font-bold text-center text-brand-dark mb-8">Web Sitesi Paketleri SSS</h2>
                    <div className="space-y-4">
                        {PRICING_FAQS.map((faq, idx) => {
                            const uniqueIdx = idx + 1000;
                            return (
                                <div key={uniqueIdx} className={`border border-gray-200 rounded-lg overflow-hidden transition-colors duration-300 ${openFaq === uniqueIdx ? 'bg-[#3e73fe] border-[#3e73fe]' : 'bg-white hover:border-brand-blue/30'}`}>
                                    <button
                                        onClick={() => toggleFaq(uniqueIdx)}
                                        className={`w-full flex justify-between items-center p-6 text-left transition-colors ${openFaq === uniqueIdx ? 'text-white' : 'text-brand-dark'}`}
                                    >
                                        <span className="font-medium text-lg">{faq.question}</span>
                                        {openFaq === uniqueIdx ? <ChevronUp className="flex-shrink-0 ml-4" /> : <ChevronDown className="flex-shrink-0 ml-4" />}
                                    </button>
                                    <div className={`transition-all duration-300 ease-in-out ${openFaq === uniqueIdx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className={`p-6 pt-0 ${openFaq === uniqueIdx ? 'text-white' : 'text-gray-700'}`}>
                                            {faq.answer}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Mobile App FAQ List */}
            <section className="py-12 bg-white">
                <div className="container mx-auto px-4 md:px-8 max-w-4xl">
                    <h2 className="text-2xl font-bold text-center text-brand-dark mb-8">Mobil Uygulama Paketleri SSS</h2>
                    <div className="space-y-4">
                        {MOBILE_APP_FAQS.map((faq, idx) => {
                            const uniqueIdx = idx + 2000;
                            return (
                                <div key={uniqueIdx} className={`border border-gray-200 rounded-lg overflow-hidden transition-colors duration-300 ${openFaq === uniqueIdx ? 'bg-[#3e73fe] border-[#3e73fe]' : 'bg-white hover:border-brand-blue/30'}`}>
                                    <button
                                        onClick={() => toggleFaq(uniqueIdx)}
                                        className={`w-full flex justify-between items-center p-6 text-left transition-colors ${openFaq === uniqueIdx ? 'text-white' : 'text-brand-dark'}`}
                                    >
                                        <span className="font-medium text-lg">{faq.question}</span>
                                        {openFaq === uniqueIdx ? <ChevronUp className="flex-shrink-0 ml-4" /> : <ChevronDown className="flex-shrink-0 ml-4" />}
                                    </button>
                                    <div className={`transition-all duration-300 ease-in-out ${openFaq === uniqueIdx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className={`p-6 pt-0 ${openFaq === uniqueIdx ? 'text-white' : 'text-gray-700'}`}>
                                            {faq.answer}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Enterprise FAQ List */}
            <section className="py-12 bg-brand-light">
                <div className="container mx-auto px-4 md:px-8 max-w-4xl">
                    <h2 className="text-2xl font-bold text-center text-brand-dark mb-8">IT Danışmanlık ve Özel Yazılım SSS</h2>
                    <div className="space-y-4">
                        {ENTERPRISE_FAQS.map((faq, idx) => {
                            const uniqueIdx = idx + 3000;
                            return (
                                <div key={uniqueIdx} className={`border border-gray-200 rounded-lg overflow-hidden transition-colors duration-300 ${openFaq === uniqueIdx ? 'bg-[#3e73fe] border-[#3e73fe]' : 'bg-white hover:border-brand-blue/30'}`}>
                                    <button
                                        onClick={() => toggleFaq(uniqueIdx)}
                                        className={`w-full flex justify-between items-center p-6 text-left transition-colors ${openFaq === uniqueIdx ? 'text-white' : 'text-brand-dark'}`}
                                    >
                                        <span className="font-medium text-lg">{faq.question}</span>
                                        {openFaq === uniqueIdx ? <ChevronUp className="flex-shrink-0 ml-4" /> : <ChevronDown className="flex-shrink-0 ml-4" />}
                                    </button>
                                    <div className={`transition-all duration-300 ease-in-out ${openFaq === uniqueIdx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className={`p-6 pt-0 ${openFaq === uniqueIdx ? 'text-white' : 'text-gray-700'}`}>
                                            {faq.answer}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Faq;
